// src/services/api/dashboard.service.ts
import { authService } from '../auth.service';
import { ReferralData, ReferralActivation } from "../../types/application.types";
import { getServerUrl, getCurrentApiUrl } from '../../config/server.config';

interface MasterCardData {
    success: boolean;
    data: {
        data: {
            data: any
        }
    }
}
interface InitTopUpData {
    operationId: string;
    operationNumber: string;
    status: string;
    masterWalletAddress: string;
    masterWalletType: string;
}
interface QuoteData {
    operationId: string;
    cryptoAmount: string;
    cryptoSymbol: string;
    exchangeRate: number;
    fiatAmount: number;
    fiatCurrency: string;
    networkFee: number
    networkFeeFiat: number
    commissionPercentage: number,
    commissionAmount: number;
    netFiatAmount: number;
    expiresAt: string;
}
type Card = {
    id: number;
    exp: string; // e.g., "2030-06-30"
    type: 'VIRTUAL' | 'PHYSICAL'; // if there are other types, you can extend this union
    activationDate: string | null;
    configId: string;
    externalCardId: string;
    dcUserId: number;
    dcCorporationId: number | null;
    balanceId: string;
    cardNo?: string;
    maskedPan?: string;
};

type PaginationLink = {
    url: string | null;
    label: string;
    active: boolean;
};

type Links = {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
};

type Meta = {
    current_page: number;
    from: number;
    last_page: number;
    links: PaginationLink[];
    path: string;
    per_page: number;
    to: number;
    total: number;
};

type CardsResponse = {
    success: boolean;
    message: string;
    data: {
        data: Card[];
        links: Links;
        meta: Meta;
    };
};
type ResponseCardBalance = {
    success: boolean;
    data: {
        balance: string;
        currency: string;
    }
}
interface TopUpData {
    amount: number,
    railAccountId: string
}
interface ResponseTopUpData {
    success: boolean,
    message: string,
    data: any[];
}
// interface InitGkhanData {

// }
class SettingService {
    private get baseUrl(): string {
        return getServerUrl(true);
    }

    async getCryptoCurrency(){
        const response = await fetch(
        'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,binancecoin,tether,tron,usd-coin&vs_currencies=usd&precision=6'
      );
      const data = await response.json();
      const cryptoPrices = {
        BTC: data.bitcoin?.usd || 0,
        ETH: data.ethereum?.usd || 0,
        TRX: data.tron?.usd || 0.05,
        BNB: data.binancecoin?.usd || 0,  
      };
      return cryptoPrices;
    }
    async getFiatCurrency(){
        const response = await fetch(
        'https://open.er-api.com/v6/latest/USD'
      );
      const data = await response.json();
      const fiatPrices = {
        USD: 1.0, // Base 1.0
        EUR: 1 / (parseFloat(data.rates.EUR) || 1.08),
        GBP: 1 / (data.rates.GBP || 1.27),
        AED: 1 / (data.rates.AED || 3.67),
        MEX: 1 / (data.rates.MXN || 20.0),
        PAN: 1.0 // Balboa panameño tiene paridad con USD
      };
      return fiatPrices;
    }

    async getMasterCardData(): Promise<MasterCardData> {
        const response = await fetch(`${this.baseUrl}/providers/verestro/cards`, {
            method: 'GET',
            headers: {
                'Authorization': authService.getAuthorizationHeader(),
                'Content-Type': 'application/json'
            },
        });
        return await response.json()
    }
    async getCardInit(data): Promise<InitTopUpData> {
        const response = await fetch(`${this.baseUrl}/crypto/operations/initiate`, {
            method: 'POST',
            headers: {
                'Authorization': authService.getAuthorizationHeader(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        const initdata = await response.json();
        return initdata.data;
    }
    async getCardQuote(data): Promise<QuoteData> {
        const response = await fetch(`${this.baseUrl}/crypto/operations/quote`, {
            method: 'POST',
            headers: {
                'Authorization': authService.getAuthorizationHeader(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        const quotedata = await response.json();
        return quotedata.data;
    }
    async confirmCard(id: string): Promise<any> {
        const confirmResponse = await
            fetch(`${this.baseUrl}/crypto/operations/confirm`, {
                method: 'POST',
                headers: {
                    'Authorization': authService.getAuthorizationHeader(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    operationId: id
                })
            });
        const confirmdata = await confirmResponse.json();
        return confirmdata.data;
    }
    async fetchCardBalance(cardId: string): Promise<ResponseCardBalance> {
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${getCurrentApiUrl()}/providers/verestro/cards/${cardId}/balance`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Error fetching card balance: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {

            console.log(error);
        }
    };
    async getCardData(): Promise<any> {
        try {
            const response = await fetch(`${getCurrentApiUrl()}/providers/verestro/cards`, {
                headers: {
                    'Authorization': authService.getAuthorizationHeader(),
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch cards: ${response.status}`);
            }

            const data = await response.json();
            if (data.success && data.data) {
                if (data.data.data && Array.isArray(data.data.data)) {
                    return data.data.data;
                } else if (data.data.content && Array.isArray(data.data.content)) {
                    return data.data.content;
                } else if (Array.isArray(data.data)) {
                    return data.data;
                }
            }
            return [];
        } catch (error) {
            console.error('Error activating referrer:', error);
        }
    }
    async getCardItem(card:Card): Promise<Card> {
        try {
            // Consulta detallada igual que en CardDetail.tsx
            const response = await fetch(`${getCurrentApiUrl()}/providers/verestro/cards/${card.id}`, {
              headers: {
                'Authorization': authService.getAuthorizationHeader(),
                'Content-Type': 'application/json'
              }
            });
            
            if (!response.ok) return card;
            
            const data = await response.json();
            
            if (data.success && data.data) {
              return {
                ...card,
                // Priorizar cardNo sobre maskedPan para obtener el número completo si está disponible
                cardNo: data.data.cardNo || data.data.cardNumber,
                maskedPan: data.data.maskedPan
              };
            }
            
            return card;
          } catch (error) {
            console.error(`Error fetching details for card ${card.id}:`, error);
            return card;
          }
    }
}

export const settingService = new SettingService();